import {
  ExternalLinkType,
  FeedbackQuestionType,
  LegacyProductCode,
  ProductBehavior,
} from '@up2stay/up2promo-api-client';
import type {
  ProductFeedbackInputDto,
  ProductTargetInputDto,
} from '@up2stay/up2promo-api-client';

import { toExternalHref } from '../profile/to-external-href';
import { productTypeToBehaviorType } from './product-behavior-codes';
import type {
  ProductFeedbackQuestionDraft,
  ProductFormLocation,
  ProductFormValues,
} from './product-form.types';
import { resolveIntendedBehaviorForFormWrite } from './product-identity.utils';

export type ProductRoutingMode =
  | 'profile'
  | 'location'
  | 'instagramUsername'
  | 'tiktokUsername'
  | 'customUrl'
  | 'behaviourSelect';

const LOCATION_BEHAVIOUR_TYPES: ProductBehavior[] = [
  ProductBehavior.GOOGLE_REVIEW,
  ProductBehavior.INSTAGRAM,
  ProductBehavior.MENU,
  ProductBehavior.FACEBOOK,
  ProductBehavior.TIKTOK,
];

export function isLocationBehaviourType(
  type: ProductBehavior | string | undefined,
): boolean {
  return LOCATION_BEHAVIOUR_TYPES.includes(type as ProductBehavior);
}

export function isLocationBackedProductType(
  productType: string | undefined | null,
): boolean {
  return (
    productType === LegacyProductCode.GO ||
    productType === LegacyProductCode.IG ||
    productType === LegacyProductCode.ME ||
    productType === LegacyProductCode.FB ||
    productType === LegacyProductCode.TT
  );
}

export function getRoutingOptions(
  knownProductType?: string | null,
): ProductRoutingMode[] {
  if (!knownProductType || knownProductType === LegacyProductCode.CU) {
    return ['behaviourSelect'];
  }
  switch (knownProductType) {
    case LegacyProductCode.BC:
      return ['profile'];
    case LegacyProductCode.IG:
      return ['location', 'instagramUsername', 'customUrl'];
    case LegacyProductCode.TT:
      return ['location', 'tiktokUsername', 'customUrl'];
    case LegacyProductCode.GO:
    case LegacyProductCode.FB:
    case LegacyProductCode.ME:
      return ['location', 'customUrl'];
    default:
      return ['behaviourSelect'];
  }
}

export function isLegacyUrlOnlyProduct(
  knownProductType: string | undefined | null,
  targetKind: ProductBehavior | string | undefined,
  targetUrl?: string,
): boolean {
  if (
    !knownProductType ||
    knownProductType === LegacyProductCode.CU ||
    knownProductType === LegacyProductCode.BC
  ) {
    return false;
  }
  if (targetKind !== ProductBehavior.URL) return false;

  // Intentional username destinations use URL type — not a “legacy custom link”.
  if (
    knownProductType === LegacyProductCode.IG &&
    targetUrl &&
    isInstagramUsernameDestination(targetUrl)
  ) {
    return false;
  }
  if (
    knownProductType === LegacyProductCode.TT &&
    targetUrl &&
    isTiktokUsernameDestination(targetUrl)
  ) {
    return false;
  }

  return true;
}

const INSTAGRAM_HOST_RE = /instagram\.com/i;
const TIKTOK_HOST_RE = /tiktok\.com/i;

/**
 * Accepts absolute http(s) URLs and protocol-less hosts like `example.com`
 * (normalized via `toExternalHref` before validation).
 * Matches backend `@IsUrl()` closely enough to avoid autosave 422s while typing.
 */
export function isValidHttpUrl(raw: string): boolean {
  const normalized = toExternalHref(raw);
  if (!normalized) return false;
  try {
    const url = new URL(normalized);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return false;
    const host = url.hostname;
    if (!host || !host.includes('.')) return false;
    return true;
  } catch {
    return false;
  }
}

/** Normalize a user-entered http(s) URL; empty when blank or invalid. */
export function normalizeHttpUrl(raw: string): string {
  const normalized = toExternalHref(raw);
  return isValidHttpUrl(normalized) ? normalized : '';
}

export function isTargetUrlFieldRequired(
  values: ProductFormValues,
  knownProductType?: string | null,
  routingMode?: ProductRoutingMode,
): boolean {
  const mode = routingMode ?? inferRoutingMode(values, knownProductType);
  return (
    mode === 'customUrl' ||
    (mode === 'behaviourSelect' && values.targetKind === ProductBehavior.URL)
  );
}

export function getTargetUrlFieldError(
  targetUrl: string,
  options?: { required?: boolean; showRequired?: boolean },
): 'required' | 'invalid' | undefined {
  const trimmed = targetUrl.trim();
  if (!trimmed) {
    return options?.required && options?.showRequired ? 'required' : undefined;
  }
  if (!isValidHttpUrl(trimmed)) {
    return 'invalid';
  }
  return undefined;
}

export function isTargetUrlFieldVisible(
  routingMode: ProductRoutingMode,
  targetKind: ProductBehavior | string,
): boolean {
  return (
    routingMode === 'customUrl' ||
    (routingMode === 'behaviourSelect' && targetKind === ProductBehavior.URL)
  );
}

export function getTargetUrlFieldErrorMessage(
  targetUrl: string,
  messages?: {
    required?: string;
    invalid?: string;
  },
  options?: { required?: boolean; showRequired?: boolean },
): string | undefined {
  const kind = getTargetUrlFieldError(targetUrl, options);
  if (kind === 'required') {
    return messages?.required ?? 'Enter a link URL.';
  }
  if (kind === 'invalid') {
    return (
      messages?.invalid ??
      'Enter a valid URL (for example example.com or https://example.com).'
    );
  }
  return undefined;
}

export function parseInstagramUsername(targetUrl: string): string {
  const trimmed = targetUrl.trim();
  if (!trimmed) return '';

  // Bare handle stored as `@ilias` or `ilias` (not a URL).
  if (isBareSocialHandle(trimmed)) {
    return trimmed.replace(/^@/, '');
  }

  try {
    const url = new URL(
      /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`,
    );
    if (!INSTAGRAM_HOST_RE.test(url.hostname)) return '';
    const segment = url.pathname.replace(/^\/+|\/+$/g, '').split('/')[0];
    return segment?.replace(/^@/, '') ?? '';
  } catch {
    return '';
  }
}

export function buildInstagramTargetUrl(username: string): string {
  const clean = username.trim().replace(/^@/, '');
  return clean ? `https://instagram.com/${clean}` : '';
}

/** Display form for username fields (`@ilias`). */
export function formatSocialUsernameDisplay(username: string): string {
  const clean = username.trim().replace(/^@/, '');
  return clean ? `@${clean}` : '';
}

export function parseTiktokUsername(targetUrl: string): string {
  const trimmed = targetUrl.trim();
  if (!trimmed) return '';

  if (isBareSocialHandle(trimmed)) {
    return trimmed.replace(/^@/, '');
  }

  try {
    const url = new URL(
      /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`,
    );
    if (!TIKTOK_HOST_RE.test(url.hostname)) return '';
    const path = url.pathname.replace(/^\/+|\/+$/g, '');
    const match = path.match(/^@?([^/]+)/);
    return match?.[1]?.replace(/^@/, '') ?? '';
  } catch {
    return '';
  }
}

export function buildTiktokTargetUrl(username: string): string {
  const clean = username.trim().replace(/^@/, '');
  return clean ? `https://www.tiktok.com/@${clean}` : '';
}

/**
 * `@handle` / `handle` without scheme or path — used by older client storage
 * and detail display. Rejects domain-like strings (`example.com`).
 */
function isBareSocialHandle(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed || trimmed.includes('/') || /^https?:\/\//i.test(trimmed)) {
    return false;
  }
  if (trimmed.startsWith('@')) {
    const handle = trimmed.slice(1);
    return /^[\w.]+$/.test(handle) && handle.length > 0 && handle.length <= 100;
  }
  // Bare without @: allow word chars + dots, but not a TLD-looking suffix.
  if (/\.[a-z]{2,}$/i.test(trimmed)) return false;
  return /^[\w.]+$/.test(trimmed) && trimmed.length <= 100;
}

function looksLikeInstagramUrl(targetUrl: string): boolean {
  if (!targetUrl.trim()) return false;
  try {
    const url = new URL(
      targetUrl.trim().startsWith('http')
        ? targetUrl.trim()
        : `https://${targetUrl.trim()}`,
    );
    return INSTAGRAM_HOST_RE.test(url.hostname);
  } catch {
    return false;
  }
}

function looksLikeTiktokUrl(targetUrl: string): boolean {
  if (!targetUrl.trim()) return false;
  try {
    const url = new URL(
      targetUrl.trim().startsWith('http')
        ? targetUrl.trim()
        : `https://${targetUrl.trim()}`,
    );
    return TIKTOK_HOST_RE.test(url.hostname);
  } catch {
    return false;
  }
}

/** Instagram profile destination (URL or bare `@handle`), not a post/reel. */
export function isInstagramUsernameDestination(targetUrl: string): boolean {
  const trimmed = targetUrl.trim();
  if (!trimmed) return false;
  if (
    trimmed.includes('/p/') ||
    trimmed.includes('/reel/') ||
    trimmed.includes('/tv/')
  ) {
    return false;
  }
  if (isBareSocialHandle(trimmed)) return true;
  if (!looksLikeInstagramUrl(trimmed)) return false;
  return parseInstagramUsername(trimmed).length > 0;
}

export function isTiktokUsernameDestination(targetUrl: string): boolean {
  const trimmed = targetUrl.trim();
  if (!trimmed) return false;
  if (trimmed.includes('/video/')) return false;
  if (isBareSocialHandle(trimmed)) return true;
  if (!looksLikeTiktokUrl(trimmed)) return false;
  return parseTiktokUsername(trimmed).length > 0;
}

export function inferRoutingMode(
  values: ProductFormValues,
  knownProductType?: string | null,
): ProductRoutingMode {
  const options = getRoutingOptions(knownProductType);
  if (options.length === 1) return options[0]!;

  if (knownProductType === LegacyProductCode.BC) return 'profile';

  if (isLocationBehaviourType(values.targetKind) && values.locationId) {
    return 'location';
  }

  if (
    values.targetKind === ProductBehavior.URL &&
    knownProductType === LegacyProductCode.IG &&
    values.targetUrl.trim()
  ) {
    if (isInstagramUsernameDestination(values.targetUrl)) {
      return 'instagramUsername';
    }
    return 'customUrl';
  }

  if (
    values.targetKind === ProductBehavior.URL &&
    knownProductType === LegacyProductCode.TT &&
    values.targetUrl.trim()
  ) {
    if (isTiktokUsernameDestination(values.targetUrl)) {
      return 'tiktokUsername';
    }
    return 'customUrl';
  }

  if (isLocationBehaviourType(values.targetKind)) {
    return 'location';
  }

  if (values.targetKind === ProductBehavior.URL) {
    return 'customUrl';
  }

  if (values.targetKind === ProductBehavior.PROFILE) {
    return 'profile';
  }

  if (!knownProductType || knownProductType === LegacyProductCode.CU) {
    return 'behaviourSelect';
  }

  return options.includes('location') ? 'location' : 'customUrl';
}

/**
 * Mode the redirect summary should describe. Selectable products always
 * report `behaviourSelect`; the real target is `targetKind` (DEV-572: a
 * product switched to a profile showed "—" or the old URL).
 */
export function resolveSummaryRoutingMode(
  mode: ProductRoutingMode,
  targetKind: ProductFormValues['targetKind'],
): ProductRoutingMode {
  if (mode !== 'behaviourSelect') return mode;
  if (targetKind === ProductBehavior.PROFILE) return 'profile';
  if (isLocationBehaviourType(targetKind)) return 'location';
  return 'customUrl';
}

export function applyRoutingMode(
  mode: ProductRoutingMode,
  values: ProductFormValues,
  knownProductType?: string | null,
): Partial<ProductFormValues> {
  const effectiveType =
    knownProductType && knownProductType !== LegacyProductCode.CU
      ? knownProductType
      : null;

  switch (mode) {
    case 'profile':
      return {
        targetKind: ProductBehavior.PROFILE,
        locationId: null,
        targetUrl: '',
      };
    case 'location': {
      const behaviour = effectiveType
        ? productTypeToBehaviorType(effectiveType)
        : isLocationBehaviourType(values.targetKind)
          ? values.targetKind
          : ProductBehavior.GOOGLE_REVIEW;
      return {
        targetKind: behaviour,
        locationId: values.locationId,
        targetUrl: '',
      };
    }
    case 'instagramUsername': {
      const username = parseInstagramUsername(values.targetUrl);
      return {
        targetKind: ProductBehavior.URL,
        locationId: null,
        targetUrl: buildInstagramTargetUrl(username || values.targetUrl),
      };
    }
    case 'tiktokUsername': {
      const username = parseTiktokUsername(values.targetUrl);
      return {
        targetKind: ProductBehavior.URL,
        locationId: null,
        targetUrl: buildTiktokTargetUrl(username || values.targetUrl),
      };
    }
    case 'customUrl':
      return {
        targetKind: ProductBehavior.URL,
        locationId: null,
        targetUrl: values.targetUrl,
      };
    case 'behaviourSelect':
    default:
      return {};
  }
}

export function defaultTargetKindForProduct(
  productType: string | undefined | null,
  existingType?: ProductBehavior | string | null,
): ProductBehavior | string {
  if (existingType) return existingType;
  if (!productType || productType === LegacyProductCode.CU) {
    return ProductBehavior.URL;
  }
  return productTypeToBehaviorType(productType);
}

export type LocationRoutingWarning =
  | 'missingGooglePlace'
  | 'missingInstagram'
  | 'missingFacebook'
  | 'missingTiktok'
  | 'missingMenu';

export type LocationReadinessIssue = LocationRoutingWarning;

function locationHasExternalLink(
  location: ProductFormLocation,
  type: ExternalLinkType,
): boolean {
  return !!location.externalLinks?.some(
    (e) => e.type === type && e.value?.trim(),
  );
}

export function getLocationReadinessIssue(
  knownProductType: string | undefined | null,
  location: ProductFormLocation | undefined,
): LocationReadinessIssue | null {
  if (!location) return null;
  switch (knownProductType) {
    case LegacyProductCode.GO:
      return location.reviewExternalIds?.google?.trim()
        ? null
        : 'missingGooglePlace';
    case LegacyProductCode.IG:
      return locationHasExternalLink(location, ExternalLinkType.INSTAGRAM)
        ? null
        : 'missingInstagram';
    case LegacyProductCode.FB:
      return locationHasExternalLink(location, ExternalLinkType.FACEBOOK)
        ? null
        : 'missingFacebook';
    case LegacyProductCode.TT:
      return locationHasExternalLink(location, ExternalLinkType.TIKTOK)
        ? null
        : 'missingTiktok';
    case LegacyProductCode.ME:
      return locationHasExternalLink(location, ExternalLinkType.MENU)
        ? null
        : 'missingMenu';
    default:
      return null;
  }
}

export function locationMeetsProductRequirements(
  knownProductType: string | undefined | null,
  location: ProductFormLocation | undefined,
): boolean {
  return getLocationReadinessIssue(knownProductType, location) === null;
}

export function getLocationRoutingWarning(
  mode: ProductRoutingMode,
  knownProductType: string | undefined | null,
  location: ProductFormLocation | undefined,
): LocationRoutingWarning | null {
  if (mode !== 'location' || !location) return null;
  return getLocationReadinessIssue(knownProductType, location);
}

export function getLocationReadinessIssueForBehaviourType(
  targetKind: ProductBehavior | string,
  location: ProductFormLocation | undefined,
): LocationReadinessIssue | null {
  if (!location) return null;
  switch (targetKind) {
    case ProductBehavior.GOOGLE_REVIEW:
      return location.reviewExternalIds?.google?.trim()
        ? null
        : 'missingGooglePlace';
    case ProductBehavior.INSTAGRAM:
      return locationHasExternalLink(location, ExternalLinkType.INSTAGRAM)
        ? null
        : 'missingInstagram';
    case ProductBehavior.FACEBOOK:
      return locationHasExternalLink(location, ExternalLinkType.FACEBOOK)
        ? null
        : 'missingFacebook';
    case ProductBehavior.TIKTOK:
      return locationHasExternalLink(location, ExternalLinkType.TIKTOK)
        ? null
        : 'missingTiktok';
    case ProductBehavior.MENU:
      return locationHasExternalLink(location, ExternalLinkType.MENU)
        ? null
        : 'missingMenu';
    default:
      return null;
  }
}

/**
 * The feedback funnel (review gate, questions) exists only on Google review
 * targets (owner decision 3, ADR-0026). The backend drops funnel config for
 * any other target kind, so the editor neither shows nor sends it.
 */
export function hasFeedbackFunnel(
  values: Pick<ProductFormValues, 'targetKind'>,
): boolean {
  return values.targetKind === ProductBehavior.GOOGLE_REVIEW;
}

export type ProductFeedbackQuestionDto = {
  id?: string;
  type: FeedbackQuestionType;
  label: string;
  required: boolean;
  options?: string[];
};

export function serializeFeedbackQuestionsForApi(
  questions: ProductFeedbackQuestionDraft[],
): ProductFeedbackQuestionDto[] {
  return questions
    .filter((q) => q.label.trim())
    .slice(0, 5)
    .map((q) => ({
      ...(q.id ? { id: q.id } : {}),
      type: q.type,
      label: q.label.trim(),
      required: q.required,
      options:
        q.type === FeedbackQuestionType.SINGLE_SELECT ||
        q.type === FeedbackQuestionType.MULTI_SELECT
          ? q.optionsText
              .split('\n')
              .map((o) => o.trim())
              .filter(Boolean)
          : undefined,
    }));
}

/**
 * Nested write parts built from the form (ADR-0026, DEV-794): the label,
 * the target as a unit, the factory intent (create only; owners cannot edit
 * the item) and the Google-review funnel.
 */
export type ProductWriteFromForm = {
  label: string;
  target: ProductTargetInputDto;
  behavior: ProductBehavior;
  feedback?: ProductFeedbackInputDto;
};

function targetFromForm(
  mode: ProductRoutingMode,
  values: ProductFormValues,
): ProductTargetInputDto {
  const profile = (): ProductTargetInputDto => ({
    kind: ProductBehavior.PROFILE,
    ...(values.userId ? { userId: values.userId } : {}),
  });
  const url = (): ProductTargetInputDto => {
    const normalized = normalizeHttpUrl(values.targetUrl);
    return {
      kind: ProductBehavior.URL,
      ...(normalized && { url: normalized }),
    };
  };
  const location = (): ProductTargetInputDto => ({
    kind: values.targetKind as ProductBehavior,
    ...(values.locationId ? { locationId: values.locationId } : {}),
  });
  switch (mode) {
    case 'profile':
      return profile();
    case 'location':
      return location();
    case 'instagramUsername':
    case 'tiktokUsername':
    case 'customUrl':
      return url();
    case 'behaviourSelect':
    default:
      if (values.targetKind === ProductBehavior.PROFILE) return profile();
      if (values.targetKind === ProductBehavior.URL) return url();
      return location();
  }
}

export function buildProductWriteFromFormValues(
  values: ProductFormValues,
  knownProductType?: string | null,
  routingModeOverride?: ProductRoutingMode,
): ProductWriteFromForm {
  const mode =
    routingModeOverride ?? inferRoutingMode(values, knownProductType);
  return {
    label: values.description.trim(),
    target: targetFromForm(mode, values),
    behavior: resolveIntendedBehaviorForFormWrite(values, knownProductType),
    ...(hasFeedbackFunnel(values) && {
      feedback: {
        mode: values.feedbackMode,
        reviewThreshold: values.reviewThreshold,
        questions: serializeFeedbackQuestionsForApi(values.feedbackQuestions),
      },
    }),
  };
}

export function canSubmitForRoutingMode(params: {
  description: string;
  mode: ProductRoutingMode;
  values: ProductFormValues;
  knownProductType?: string | null;
  locations?: ProductFormLocation[];
  instagramUsername?: string;
  tiktokUsername?: string;
  isSaving?: boolean;
  /** When true, blank description is allowed (setup wizard auto-derives). */
  descriptionOptional?: boolean;
}): boolean {
  if (params.isSaving) return false;
  if (!params.descriptionOptional && !params.description.trim()) return false;

  switch (params.mode) {
    case 'profile':
      return !!params.values.userId;
    case 'location': {
      if (!params.values.locationId) return false;
      const selected = params.locations?.find(
        (l) => l.id === params.values.locationId,
      );
      const productType =
        params.knownProductType ??
        (params.values.targetKind === ProductBehavior.URL
          ? null
          : params.values.targetKind);
      if (
        productType &&
        !locationMeetsProductRequirements(productType, selected)
      ) {
        return false;
      }
      if (!productType && isLocationBehaviourType(params.values.targetKind)) {
        const issue = getLocationReadinessIssueForBehaviourType(
          params.values.targetKind,
          selected,
        );
        if (issue) return false;
      }
      return true;
    }
    case 'instagramUsername':
      return !!(
        params.instagramUsername?.trim() || params.values.targetUrl.trim()
      );
    case 'tiktokUsername':
      return !!(
        params.tiktokUsername?.trim() || params.values.targetUrl.trim()
      );
    case 'customUrl':
      return isValidHttpUrl(params.values.targetUrl);
    case 'behaviourSelect':
      if (params.values.targetKind === ProductBehavior.URL) {
        return isValidHttpUrl(params.values.targetUrl);
      }
      if (params.values.targetKind === ProductBehavior.PROFILE) {
        return !!params.values.userId;
      }
      return true;
    default:
      return true;
  }
}
